import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Video, MapPin, Phone, Mail, Info, ListChecks, Pencil } from 'lucide-react';
import { DialogForm, DisclosureChevron, MultiUserPicker, BidiText } from '../../../shared/components/common';
import { LocalizedDateInput, LocalizedTimeInput, TimezonePicker } from '../../../shared/components';
import { useCreateInterview, useCreateCandidateInterview, InterviewCreate } from '../hooks/useInterviews';
import { useCandidates } from '../hooks/useCandidates';
import { useApplicationsByCandidate, useCreateApplication } from '../hooks/useApplications';
import { useJobs } from '../hooks/useJobs';
import { useJobStageQuestions } from '../hooks/useJobStageQuestions';
import { useNotification, useUserPreferences } from '../../../shared/context';
import { useApiError } from '../../../shared/hooks/useApiError';
import { useAuth } from '../../../shared/auth';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { InterviewQuestionEditor } from './InterviewQuestionEditor';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Separator } from '../../../components/ui/separator';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { cn } from '../../../lib/utils';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useScorecardTemplates } from '../hooks/useScorecardTemplates';
import { useOffices } from '../../../shared/hooks/useOffices';
import type { Candidate, ScorecardTemplate, JobPosting, PlannedQuestion } from '../services/recruitingApi';
import { isTerminalJobStatus } from '../utils/jobStatus';

interface InterviewFormProps {
  open: boolean;
  onClose: () => void;
  /** Pre-selected application ID (when opening from candidate context) */
  applicationId?: string;
}

const INTERVIEW_FORMATS = ['phone', 'video', 'onsite'] as const;
const INTERVIEW_TYPES = ['phone_screen', 'technical', 'panel', 'final', 'reference_call', 'test_exam'] as const;
const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];

interface FormData {
  candidateId: string;
  applicationId: string;
  format: string;
  interview_type: string;
  title: string;
  scheduled_date: string | null;
  scheduled_time: string | null;
  timezone: string;
  duration_minutes: number;
  office_id: string;
  room: string;
  video_link: string;
  scorecard_template_id: string;
  instructions: string;
  candidate_prep: string;
  participants: string[];
  send_candidate_invite: boolean;
  send_self_invite: boolean;
}

export function InterviewForm({ open, onClose, applicationId: preselectedApplicationId }: InterviewFormProps) {
  const { t } = useTranslation('recruiting');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { handleMutationError } = useApiError({ showToast: false });
  const { formatOptions } = useUserPreferences();
  const { user } = useAuth();

  const [candidateSearchOpen, setCandidateSearchOpen] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [plannedQuestions, setPlannedQuestions] = useState<PlannedQuestion[]>([]);
  const [questionsExpanded, setQuestionsExpanded] = useState(false);
  const [jobPickerExpanded, setJobPickerExpanded] = useState(false);
  const [pendingNewJobId, setPendingNewJobId] = useState<string | null>(null);

  const createMutation = useCreateInterview();
  const createCandidateMutation = useCreateCandidateInterview();
  const createApplicationMutation = useCreateApplication();
  const { data: scorecardTemplates } = useScorecardTemplates();
  const { data: officesData } = useOffices();
  const offices = officesData?.data || [];

  // Fetch all candidates for the search-based picker
  const { data: candidatesData } = useCandidates({
    search: candidateSearch,
    limit: 20,
  });
  const candidates = candidatesData?.data || [];

  // Fetch applications for selected candidate (to populate job/application picker)
  const { data: candidateApplications } = useApplicationsByCandidate(selectedCandidate?.id || '');

  // Active applications are the ones shown in the job picker
  const applications = useMemo(() => candidateApplications || [], [candidateApplications]);

  // Fetch all open jobs for the "apply to new job" group
  const { data: jobsData } = useJobs({ limit: 200 });
  const availableJobs = useMemo(() => {
    const appliedJobIds = new Set(applications.map((a) => a.job_posting_id));
    return (jobsData?.data || []).filter(
      (job: JobPosting) => !isTerminalJobStatus(job.status) && !appliedJobIds.has(job.id)
    );
  }, [jobsData, applications]);

  const {
    control,
    handleSubmit,
    register,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    mode: 'onBlur',
    defaultValues: {
      candidateId: '',
      applicationId: preselectedApplicationId || '',
      format: 'video',
      interview_type: '',
      title: '',
      scheduled_date: null,
      scheduled_time: null,
      timezone: formatOptions.timezone,
      duration_minutes: 60,
      office_id: '',
      room: '',
      video_link: '',
      scorecard_template_id: '',
      instructions: '',
      candidate_prep: '',
      participants: user?.id ? [user.id] : [],
      send_candidate_invite: false,
      send_self_invite: false,
    },
  });

  const format = watch('format');
  const watchedApplicationId = watch('applicationId');

  // Derive selected application for stage context banner
  const selectedApplication = useMemo(() => {
    if (!watchedApplicationId) return null;
    return applications.find((a) => a.id === watchedApplicationId) || null;
  }, [watchedApplicationId, applications]);

  // Derive the pending new job title for the info banner
  const pendingNewJob = useMemo(() => {
    if (!pendingNewJobId) return null;
    return availableJobs.find((j: JobPosting) => j.id === pendingNewJobId) || null;
  }, [pendingNewJobId, availableJobs]);

  // Fetch job-stage questions for the selected application's stage
  const { data: jobStageQuestionsData } = useJobStageQuestions(
    selectedApplication?.job_posting_id || '',
    selectedApplication?.stage_id || '',
  );

  // Seed planned questions from job-stage questions when application changes
  useEffect(() => {
    if (!watchedApplicationId) {
      setPlannedQuestions([]);
      return;
    }
    const questions = jobStageQuestionsData?.questions?.questions;
    if (questions && questions.length > 0) {
      setPlannedQuestions(questions);
    } else {
      // Fallback to inherited (template) questions
      const inherited = jobStageQuestionsData?.inherited_questions;
      if (inherited && inherited.length > 0) {
        setPlannedQuestions(inherited);
      } else {
        setPlannedQuestions([]);
      }
    }
  }, [watchedApplicationId, jobStageQuestionsData]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      reset({
        candidateId: '',
        applicationId: preselectedApplicationId || '',
        format: 'video',
        interview_type: '',
        title: '',
        scheduled_date: null,
        scheduled_time: null,
        timezone: formatOptions.timezone,
        duration_minutes: 60,
        office_id: '',
        room: '',
        video_link: '',
        scorecard_template_id: '',
        instructions: '',
        candidate_prep: '',
        participants: user?.id ? [user.id] : [],
        send_candidate_invite: false,
        send_self_invite: false,
      });
      setSelectedCandidate(null);
      setCandidateSearch('');
      setPlannedQuestions([]);
      setQuestionsExpanded(false);
      setJobPickerExpanded(false);
      setPendingNewJobId(null);
      // Auto-open candidate picker when dialog opens (if no preselected application)
      if (!preselectedApplicationId) {
        // Small delay to ensure dialog is fully rendered
        setTimeout(() => setCandidateSearchOpen(true), 100);
      }
    }
  }, [open, reset, preselectedApplicationId, formatOptions.timezone]);

  // Auto-select the application when candidate has exactly one active application
  useEffect(() => {
    if (!watchedApplicationId && !pendingNewJobId && applications.length > 0) {
      const activeApps = applications.filter((a) => a.status === 'active');
      if (activeApps.length === 1) {
        setValue('applicationId', activeApps[0].id);
      }
    }
  }, [applications, watchedApplicationId, pendingNewJobId, setValue]);

  // Auto-select scorecard template and interview type from application's current stage
  useEffect(() => {
    if (!watchedApplicationId || !applications) return;
    const app = applications.find((a) => a.id === watchedApplicationId);
    if (app?.stage?.scorecard_template_id) {
      setValue('scorecard_template_id', app.stage.scorecard_template_id);
    }
    if (app?.stage?.default_interview_type) {
      setValue('interview_type', app.stage.default_interview_type);
    }
  }, [watchedApplicationId, applications, setValue]);

  const handleCandidateSelect = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setValue('candidateId', candidate.id);
    setValue('applicationId', ''); // Reset application when candidate changes
    setPendingNewJobId(null);
    setCandidateSearchOpen(false);
    setJobPickerExpanded(false);
  };

  // Handle selecting a new job (deferred — application created on submit)
  const handleSelectNewJob = (jobId: string) => {
    setPendingNewJobId(jobId);
    setValue('applicationId', ''); // Clear any existing application selection
    setJobPickerExpanded(false);
  };

  const onSubmit = async (data: FormData) => {
    if (!data.candidateId && !preselectedApplicationId) {
      showError(t('interviews.errors.candidateRequired'));
      return;
    }

    try {
      // If a new job was selected, create the application first
      let applicationId = data.applicationId;
      if (pendingNewJobId && data.candidateId) {
        const newApp = await createApplicationMutation.mutateAsync({
          candidate_id: data.candidateId,
          job_posting_id: pendingNewJobId,
        });
        applicationId = newApp.id;
      }

      // Combine date + time into ISO scheduled_at (both are required fields)
      const scheduled_at = `${data.scheduled_date}T${data.scheduled_time}:00`;

      const payload: InterviewCreate = {
        format: data.format,
        interview_type: data.interview_type || undefined,
        title: data.title || undefined,
        scheduled_at,
        timezone: data.timezone || undefined,
        duration_minutes: data.duration_minutes,
        office_id: data.office_id || undefined,
        room: data.room || undefined,
        video_link: data.video_link || undefined,
        scorecard_template_id: data.scorecard_template_id || undefined,
        instructions: data.instructions || undefined,
        candidate_prep: data.candidate_prep || undefined,
        planned_questions: plannedQuestions.length > 0 ? plannedQuestions : undefined,
        participants: data.participants.length > 0
          ? data.participants.map(userId => ({ user_id: userId, role: 'interviewer' }))
          : undefined,
        send_candidate_invite: data.send_candidate_invite || undefined,
        send_self_invite: data.send_self_invite || undefined,
      };

      let result;
      if (applicationId) {
        // Create via application (job-specific interview)
        result = await createMutation.mutateAsync({ applicationId, data: payload });
      } else {
        // Create directly for candidate (no specific job)
        result = await createCandidateMutation.mutateAsync({ candidateId: data.candidateId, data: payload });
      }
      showSuccess(t('interviews.created'));
      onClose();
      navigate(`/recruiting/interviews/${result.id}`);
    } catch (error) {
      handleMutationError(error, setError);
    }
  };

  const getFormatIcon = (fmt: string) => {
    switch (fmt) {
      case 'phone': return <Phone className="h-4 w-4" />;
      case 'video': return <Video className="h-4 w-4" />;
      case 'onsite': return <MapPin className="h-4 w-4" />;
      default: return null;
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('interviews.scheduleInterview')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={tCommon('app.create')}
      cancelLabel={tCommon('app.cancel')}
      isLoading={isSubmitting}
      maxWidth="md"
    >
      <div className="grid gap-6">
        {/* Candidate and Job Selection (only if no preselected application) */}
        {!preselectedApplicationId && (
          <>
            {/* Candidate Picker (first step) */}
            <div className="space-y-2">
              <Label required>
                {t('interviews.fields.candidate')}
              </Label>
              <Popover open={candidateSearchOpen} onOpenChange={setCandidateSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={candidateSearchOpen}
                    className="w-full justify-between"
                  >
                    {selectedCandidate
                      ? <BidiText>{`${selectedCandidate.first_name} ${selectedCandidate.last_name}`}</BidiText>
                      : t('interviews.selectCandidate')}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder={t('interviews.searchCandidates')}
                      value={candidateSearch}
                      onValueChange={setCandidateSearch}
                      autoFocus
                    />
                    <CommandList>
                      <CommandEmpty>{t('interviews.noCandidatesFound')}</CommandEmpty>
                      <CommandGroup>
                        {candidates.map((candidate) => (
                          <CommandItem
                            key={candidate.id}
                            value={candidate.id}
                            onSelect={() => handleCandidateSelect(candidate)}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedCandidate?.id === candidate.id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            <div className="flex flex-col">
                              <span><BidiText>{candidate.first_name} {candidate.last_name}</BidiText></span>
                              {candidate.current_title && (
                                <span className="text-xs text-muted-foreground">
                                  <BidiText>
                                    {candidate.current_company
                                      ? `${candidate.current_title} at ${candidate.current_company}`
                                      : candidate.current_title}
                                  </BidiText>
                                </span>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Job Context (after candidate is selected) */}
            {selectedCandidate && (() => {
              const activeApps = applications.filter((a) => a.status === 'active');
              const appCount = activeApps.length;

              // Case A: No applications — show text + change button
              if (appCount === 0 && !jobPickerExpanded) {
                return (
                  <div className="space-y-2">
                    <Label>{t('interviews.fields.job')}</Label>
                    <div className="flex items-center gap-2 p-2.5 rounded-md border bg-muted/30">
                      <span className="text-sm text-muted-foreground flex-1">
                        {t('interviews.notAppliedToAnyJob')}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() => setJobPickerExpanded(true)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              }

              // Case B: One application — show text with link + change button
              if (appCount === 1 && !jobPickerExpanded) {
                const app = activeApps[0];
                return (
                  <div className="space-y-2">
                    <Label>{t('interviews.fields.job')}</Label>
                    <div className="flex items-center gap-2 p-2.5 rounded-md border bg-muted/30">
                      <span className="text-sm flex-1">
                        {t('interviews.applyingForJob', {
                          jobTitle: app.job_posting?.title || t('interviews.unknownJob'),
                        }).split(app.job_posting?.title || '')[0]}
                        <Link
                          to={`/recruiting/jobs/${app.job_posting_id}`}
                          className="text-primary hover:underline"
                          target="_blank"
                        >
                          <BidiText>{app.job_posting?.title || t('interviews.unknownJob')}</BidiText>
                        </Link>
                        {app.stage && (
                          <span className="text-muted-foreground"> — <BidiText>{app.stage.name}</BidiText></span>
                        )}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() => setJobPickerExpanded(true)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              }

              // Case C: Multiple applications, or picker expanded — show full combo
              return (
                <div className="space-y-2">
                  <Label>{t('interviews.fields.job')}</Label>
                  <Controller
                    name="applicationId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={pendingNewJobId ? `new_job:${pendingNewJobId}` : (field.value || '__none__')}
                        onValueChange={(val) => {
                          if (val === '__none__') {
                            field.onChange('');
                            setPendingNewJobId(null);
                          } else if (val.startsWith('new_job:')) {
                            // User selected a new job — defer application to submit
                            // eslint-disable-next-line i18next/no-literal-string -- internal select-value prefix token, not user-facing
                            const jobId = val.replace('new_job:', '');
                            handleSelectNewJob(jobId);
                          } else {
                            field.onChange(val);
                            setPendingNewJobId(null);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('interviews.selectJob')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">
                            <span className="text-muted-foreground">{t('interviews.noSpecificJob')}</span>
                          </SelectItem>
                          {activeApps.length > 0 && (
                            <SelectGroup>
                              {activeApps.map((app) => (
                                <SelectItem key={app.id} value={app.id}>
                                  {app.job_posting?.title || t('interviews.unknownJob')}
                                  {app.stage && ` — ${app.stage.name}`}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                          {availableJobs.length > 0 && (
                            <>
                              <Separator className="my-1" />
                              <SelectGroup>
                                <SelectLabel className="text-xs">{t('interviews.applyToNewJob')}</SelectLabel>
                                {availableJobs.map((job: JobPosting) => (
                                  <SelectItem key={`new_${job.id}`} value={`new_job:${job.id}`}>
                                    {job.title}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              );
            })()}

            {/* Info banner: pending new job (will be applied on submit) */}
            {selectedCandidate && pendingNewJob && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-warning/10 border border-warning/40 text-sm">
                <Info className="h-4 w-4 text-warning mt-0.5 shrink-0" />
                <p className="text-warning">
                  {t('interviews.willBeApplied', { jobTitle: pendingNewJob.title })}
                </p>
              </div>
            )}

            {/* Info banner when creating without an application and no pending job */}
            {selectedCandidate && !watchedApplicationId && !pendingNewJobId && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-muted/50 border text-sm">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <p className="text-muted-foreground">
                  {t('interviews.noJobInterview')}
                </p>
              </div>
            )}
          </>
        )}

        {/* Stage Context Banner (hidden when a pending new job is selected) */}
        {selectedApplication?.stage && !pendingNewJobId && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-muted/50 border text-sm">
            <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="space-y-0.5">
              <p className="font-medium">
                {t('interviews.stageContext.currentStage')}: <BidiText>{selectedApplication.stage.name}</BidiText>
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground text-xs">
                {selectedApplication.stage.default_interview_type && (
                  <span>
                    {t('interviews.stageContext.suggestedType')}: {t(`interviews.types.${selectedApplication.stage.default_interview_type}`)}
                  </span>
                )}
                {selectedApplication.stage.scorecard_template_id && scorecardTemplates && (
                  <span>
                    {t('interviews.stageContext.template')}: <BidiText>{scorecardTemplates.find((tmpl: ScorecardTemplate) => tmpl.id === selectedApplication.stage?.scorecard_template_id)?.name}</BidiText>
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Interview Format, Type, and Duration */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label required>{t('interviews.fields.format')}</Label>
            <Controller
              name="format"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERVIEW_FORMATS.map((fmt) => (
                      <SelectItem key={fmt} value={fmt}>
                        <span className="flex items-center gap-2">
                          {getFormatIcon(fmt)}
                          {t(`interviews.formats.${fmt}`)}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('interviews.fields.type')}</Label>
            <Controller
              name="interview_type"
              control={control}
              render={({ field }) => (
                <Select value={field.value || '__none__'} onValueChange={(val) => field.onChange(val === '__none__' ? '' : val)}>
                  <SelectTrigger>
                    <SelectValue placeholder={tCommon('app.none')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      <span className="text-muted-foreground">{tCommon('app.none')}</span>
                    </SelectItem>
                    {INTERVIEW_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(`interviews.types.${type}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('interviews.fields.duration')}</Label>
            <Controller
              name="duration_minutes"
              control={control}
              render={({ field }) => (
                <Select
                  value={String(field.value)}
                  onValueChange={(val) => field.onChange(Number(val))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATION_OPTIONS.map((mins) => (
                      <SelectItem key={mins} value={String(mins)}>
                        {t('interviews.durationMinutes', { minutes: mins })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        {/* Title (optional custom name for the interview) */}
        <div className="space-y-2">
          <Label htmlFor="title">{t('interviews.fields.title')}</Label>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                id="title"
                placeholder={t('interviews.placeholders.title')}
              />
            )}
          />
        </div>

        {/* Scorecard Template */}
        {scorecardTemplates && scorecardTemplates.length > 0 && (
          <div className="space-y-2">
            <Label>{t('scorecards.title')}</Label>
            <Controller
              name="scorecard_template_id"
              control={control}
              render={({ field }) => (
                <Select value={field.value || '__none__'} onValueChange={(val) => field.onChange(val === '__none__' ? '' : val)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('scorecards.selectTemplate')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      <span className="text-muted-foreground">{t('scorecards.noTemplate')}</span>
                    </SelectItem>
                    {scorecardTemplates.map((tmpl: ScorecardTemplate) => (
                      <SelectItem key={tmpl.id} value={tmpl.id}>
                        {tmpl.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        )}

        {/* Interview Questions (collapsible editor) */}
        {watchedApplicationId && (
          <div className="border rounded-md">
            <Button
              type="button"
              variant="ghost"
              className="flex items-center justify-between w-full p-3 text-sm font-medium hover:bg-muted/50 transition-colors h-auto rounded-none"
              onClick={() => setQuestionsExpanded(!questionsExpanded)}
            >
              <span className="flex items-center gap-2">
                <DisclosureChevron open={questionsExpanded} />
                <ListChecks className="h-4 w-4" />
                {t('questions.interviewQuestions')}
                {plannedQuestions.length > 0 && (
                  <span className="text-xs text-muted-foreground">({plannedQuestions.length})</span>
                )}
              </span>
            </Button>
            {questionsExpanded && (
              <div className="px-3 pb-3 border-t">
                <InterviewQuestionEditor
                  questions={plannedQuestions}
                  onSave={setPlannedQuestions}
                  title=""
                />
              </div>
            )}
          </div>
        )}

        {/* Schedule Date/Time/Timezone */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Controller
              name="scheduled_date"
              control={control}
              rules={{ required: t('interviews.errors.dateRequired') }}
              render={({ field }) => (
                <LocalizedDateInput
                  value={field.value}
                  onChange={field.onChange}
                  label={t('interviews.fields.scheduledDate')}
                  required
                  error={!!errors.scheduled_date}
                />
              )}
            />
            {errors.scheduled_date && (
              <p className="text-sm text-destructive">{errors.scheduled_date.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Controller
              name="scheduled_time"
              control={control}
              rules={{ required: t('interviews.errors.timeRequired') }}
              render={({ field }) => (
                <LocalizedTimeInput
                  value={field.value}
                  onChange={field.onChange}
                  label={t('interviews.fields.scheduledTime')}
                  required
                  error={!!errors.scheduled_time}
                />
              )}
            />
            {errors.scheduled_time && (
              <p className="text-sm text-destructive">{errors.scheduled_time.message}</p>
            )}
          </div>
        </div>
        <Controller
          name="timezone"
          control={control}
          render={({ field }) => (
            <TimezonePicker
              value={field.value}
              onChange={field.onChange}
              label={t('interviews.fields.timezone')}
            />
          )}
        />

        {/* Location fields based on format */}
        {format === 'onsite' && (
          <>
            <div className="space-y-2">
              <Label>{t('interviews.fields.office')}</Label>
              <Controller
                name="office_id"
                control={control}
                render={({ field }) => (
                  <Select value={field.value || '__none__'} onValueChange={(val) => field.onChange(val === '__none__' ? '' : val)}>
                    <SelectTrigger>
                      <SelectValue placeholder={tCommon('app.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">
                        <span className="text-muted-foreground">{tCommon('app.none')}</span>
                      </SelectItem>
                      {offices.map((office) => (
                        <SelectItem key={office.id} value={office.id}>
                          {office.name}{office.city ? ` (${office.city})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('interviews.fields.room')}</Label>
              <Input {...register('room')} placeholder={t('interviews.fields.roomPlaceholder')} />
            </div>
          </>
        )}
        {format === 'video' && (
          <div className="space-y-2">
            <Label>{t('interviews.fields.videoLink')}</Label>
            <Input {...register('video_link')} placeholder="https://..." />
          </div>
        )}

        {/* Interviewers */}
        <div className="space-y-2">
          <Label required>
            {t('interviews.fields.interviewers')}
          </Label>
          <Controller
            name="participants"
            control={control}
            rules={{ validate: (v) => v.length > 0 || t('interviews.errors.interviewersRequired') }}
            render={({ field }) => (
              <MultiUserPicker
                value={field.value}
                onChange={field.onChange}
                placeholder={t('interviews.selectInterviewers')}
                filterUserTypes={['internal']}
              />
            )}
          />
          {errors.participants && (
            <p className="text-sm text-destructive">{errors.participants.message}</p>
          )}
        </div>

        {/* Instructions for interviewers */}
        <div className="space-y-2">
          <Label htmlFor="instructions">{t('interviews.fields.instructions')}</Label>
          <Controller
            name="instructions"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                id="instructions"
                placeholder={t('interviews.placeholders.instructions')}
                rows={2}
              />
            )}
          />
        </div>

        {/* Candidate preparation notes */}
        <div className="space-y-2">
          <Label htmlFor="candidate_prep">{t('interviews.fields.candidatePrep')}</Label>
          <Controller
            name="candidate_prep"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                id="candidate_prep"
                placeholder={t('interviews.placeholders.candidatePrep')}
                rows={2}
              />
            )}
          />
        </div>

        {/* Email Options */}
        <div className="space-y-3 pt-4 border-t">
          <div className="flex items-center space-x-2">
            <Controller
              name="send_candidate_invite"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="send_candidate_invite"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label htmlFor="send_candidate_invite" className="font-normal cursor-pointer flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              {t('interviews.fields.sendCandidateInvite')}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Controller
              name="send_self_invite"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="send_self_invite"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label htmlFor="send_self_invite" className="font-normal cursor-pointer flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              {t('interviews.fields.sendSelfInvite')}
            </Label>
          </div>
        </div>
      </div>
    </DialogForm>
  );
}
